import useIsMobile from '../../hooks/useIsMobile';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../../api/axios';
import PageHeader from '../../components/ui/PageHeader';

const inputStyle = (hasError = false) => ({
  width: '100%', padding: '8px 12px',
  border: `1.5px solid ${hasError ? '#EF4444' : '#E5E7EB'}`,
  borderRadius: 8, fontSize: 13, color: '#111827',
  backgroundColor: '#fff', outline: 'none', fontFamily: 'inherit',
});

const Field = ({ label, required, error, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
      {label} {required && <span style={{ color: '#EF4444' }}>*</span>}
    </label>
    {children}
    {error && <p style={{ margin: '4px 0 0', fontSize: 12, color: '#EF4444' }}>{error}</p>}
  </div>
);

const CaseFileCreate = () => {
  const isMobile = useIsMobile();
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [procedureTypes, setProcedureTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({ client_id: clientId || '', procedure_type_id: '', assigned_user_id: '', title: '', notes: '' });

  const backTo = clientId ? `/clients/${clientId}` : '/case-files';

  const focusStyle = e => { e.target.style.borderColor = '#4F63D2'; e.target.style.boxShadow = '0 0 0 3px rgba(79,99,210,0.1)'; };
  const blurStyle  = (e, err) => { e.target.style.borderColor = err ? '#EF4444' : '#E5E7EB'; e.target.style.boxShadow = 'none'; };

  useEffect(() => {
    const requests = [api.get('/procedure-types'), api.get('/users')];
    if (!clientId) requests.push(api.get('/clients', { params: { per_page: 500 } }));
    Promise.all(requests)
      .then(([ptRes, usersRes, clientsRes]) => {
        setProcedureTypes(ptRes.data);
        setUsers(usersRes.data);
        if (clientsRes) setClients(clientsRes.data.data || clientsRes.data);
      })
      .finally(() => setLoading(false));
  }, [clientId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: null }));
    if (name === 'procedure_type_id') {
      const type = procedureTypes.find(pt => pt.id === parseInt(value));
      setSelectedType(type || null);
      if (type) setForm(prev => ({ ...prev, procedure_type_id: value, title: type.name }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post('/case-files', form);
      navigate(`/case-files/${res.data.id}`);
    } catch (error) {
      if (error.response?.status === 422) setErrors(error.response.data.errors);
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <div style={{ width: 32, height: 32, border: '3px solid #EEF0FB', borderTopColor: '#4F63D2', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14, fontSize: 12, color: '#9CA3AF' }}>
        {clientId ? (
          <>
            <Link to="/clients" style={{ color: '#4F63D2', textDecoration: 'none', fontWeight: 500 }}>Clientes</Link>
            <span>›</span>
            <Link to={`/clients/${clientId}`} style={{ color: '#4F63D2', textDecoration: 'none', fontWeight: 500 }}>Cliente</Link>
          </>
        ) : (
          <Link to="/case-files" style={{ color: '#4F63D2', textDecoration: 'none', fontWeight: 500 }}>Expedientes</Link>
        )}
        <span>›</span>
        <span style={{ color: '#4B5563' }}>Nuevo Expediente</span>
      </div>

      <PageHeader
        title="Nuevo Expediente"
        subtitle="Completa los datos para crear el expediente"
        actions={
          <Link to={backTo} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, textDecoration: 'none' }}>
            <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            Regresar
          </Link>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: selectedType?.document_checklists?.length > 0 && !isMobile ? '1fr 320px' : '1fr', gap: 16, alignItems: 'start' }}>

        {/* Formulario */}
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid #F3F4F6' }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>Datos del Expediente</span>
          </div>
          <div style={{ padding: 18 }}>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0 20px' }}>
                {!clientId && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <Field label="Cliente" required error={errors.client_id?.[0]}>
                      <select name="client_id" value={form.client_id} onChange={handleChange} required
                        style={{ ...inputStyle(!!errors.client_id), cursor: 'pointer' }}
                        onFocus={focusStyle} onBlur={e => blurStyle(e, !!errors.client_id)}>
                        <option value="">Seleccionar...</option>
                        {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </Field>
                  </div>
                )}
                <Field label="Tipo de Trámite" required error={errors.procedure_type_id?.[0]}>
                  <select name="procedure_type_id" value={form.procedure_type_id} onChange={handleChange} required
                    style={{ ...inputStyle(!!errors.procedure_type_id), cursor: 'pointer' }}
                    onFocus={focusStyle} onBlur={e => blurStyle(e, !!errors.procedure_type_id)}>
                    <option value="">Seleccionar...</option>
                    {procedureTypes.map(pt => <option key={pt.id} value={pt.id}>{pt.name}</option>)}
                  </select>
                </Field>
                <Field label="Responsable" required error={errors.assigned_user_id?.[0]}>
                  <select name="assigned_user_id" value={form.assigned_user_id} onChange={handleChange} required
                    style={{ ...inputStyle(!!errors.assigned_user_id), cursor: 'pointer' }}
                    onFocus={focusStyle} onBlur={e => blurStyle(e, !!errors.assigned_user_id)}>
                    <option value="">Seleccionar...</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </Field>
                <div style={{ gridColumn: '1 / -1' }}>
                  <Field label="Título" required error={errors.title?.[0]}>
                    <input type="text" name="title" value={form.title} onChange={handleChange} required
                      style={inputStyle(!!errors.title)} onFocus={focusStyle} onBlur={e => blurStyle(e, !!errors.title)} />
                  </Field>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <Field label="Notas iniciales">
                    <textarea name="notes" rows={3} value={form.notes} onChange={handleChange}
                      style={{ ...inputStyle(), resize: 'vertical' }} onFocus={focusStyle} onBlur={e => blurStyle(e, false)} />
                  </Field>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button type="submit" disabled={submitting}
                  style={{ padding: '9px 22px', borderRadius: 8, border: 'none', backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 14, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}>
                  {submitting ? 'Creando...' : 'Crear Expediente'}
                </button>
                <Link to={backTo}
                  style={{ padding: '9px 22px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', color: '#374151', fontWeight: 600, fontSize: 14, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
                  Cancelar
                </Link>
              </div>
            </form>
          </div>
        </div>

        {/* Checklist de documentos */}
        {selectedType?.document_checklists?.length > 0 && (
          <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #F3F4F6' }}>
              <span style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>Documentos que se cargarán</span>
              <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, backgroundColor: '#EEF0FB', color: '#4F63D2', marginLeft: 8 }}>{selectedType.document_checklists.length}</span>
            </div>
            <div>
              {selectedType.document_checklists.map((doc, i) => (
                <div key={doc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 18px', borderBottom: i < selectedType.document_checklists.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                  <span style={{ fontSize: 13, color: '#374151' }}>{doc.name}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, backgroundColor: doc.required ? '#FEE2E2' : '#F3F4F6', color: doc.required ? '#991B1B' : '#374151' }}>
                    {doc.required ? 'Requerido' : 'Opcional'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CaseFileCreate;
